// 公共美术工具层 —— 全场画稿的唯一光照/色板/调色实现（规范见 DESIGN.md「视觉规范」）
//
// 为什么要有这个文件：在此之前，同一个场景跑着四条互不相干的美术管线——
// 作物有光照+接地影+出口调色、菜床有顶亮底暗+描边+水彩滤镜、背景只有水彩滤镜、
// `.dc` 摆件六项全无。于是单个画稿再用心，拼在一起还是剪贴画。
// 光照/接地影/色板/出口调色**只能有一个实现**，否则各画各的是必然结果。
//
// ⚠️ 这个文件必须在 plants.js 与 app.js **之前**加载（index.html / en/index.html / sw.js 三处）。
// plants.js 会 require 它跑单测，所以两边都要能用：浏览器读全局 ART，node 走 module.exports。
const ART = (() => {
  // ═══ 色板：9 族 × 4 档 ═══
  // 从项目原有的高频用色收敛而来，不是新配的——所以这次收敛不改变任何画面气质，
  // 只是把 200+ 个"差不多的棕色"合并成同一个棕色。
  // 四档固定语义：lit=受光面、base=固有色、dark=背光面、deep=最深处/投影侧。
  const C = {
    wood:  { lit: '#b08a5c', base: '#9c7248', dark: '#7a5836', deep: '#5d4426' },
    ink:   { lit: '#6b5138', base: '#4a3626', dark: '#2e2118', deep: '#2e2118' },
    leaf:  { lit: '#97ba7c', base: '#639647', dark: '#5e8f43', deep: '#3e7a3e' },
    soil:  { lit: '#c2a075', base: '#a08560', dark: '#6e4f30', deep: '#4e3a28' },
    warm:  { lit: '#e8a04e', base: '#e0913f', dark: '#c47a2e', deep: '#a8611f' },
    stone: { lit: '#9d9a8f', base: '#8d8a80', dark: '#6f6b60', deep: '#4a463c' },
    water: { lit: '#7ab6dd', base: '#6fa7c8', dark: '#4a90c9', deep: '#3d7ba6' },
    paper: { lit: '#f6efdd', base: '#f4f0e2', dark: '#e5e0d4', deep: '#cbb9a0' },
    gold:  { lit: '#ffd76e', base: '#f0c419', dark: '#b08a3e', deep: '#b08a3e' },
    // 青釉：雨水缸的釉色。场景里已经满是木头棕（堆肥箱/鸟屋/秋千/菜畦框），而缸就立在
    // 栅栏线上紧挨鸟屋——只有这一族冷色能一眼读出"陶的"而不和木器糊成一团。不要并进 stone。
    glaze: { lit: '#a6b7ab', base: '#84968a', dark: '#6f8277', deep: '#4c5c54' },
    // 红只保留两个：纯红（地精帽/瓢虫）与做旧红木（铁皮/独轮车斗）
    red:   { lit: '#d4614f', base: '#c0392b', dark: '#9c2f23', deep: '#7d2419' },
    rust:  { lit: '#c4604f', base: '#b04a3c', dark: '#8f3b30', deep: '#6f2e25' }
  };

  // ═══ 混色 ═══
  const P = c => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
  const mixc = (c1, c2, k) =>
    '#' + P(c1).map((v, i) => Math.round(v + (P(c2)[i] - v) * k).toString(16).padStart(2, '0')).join('');

  // ═══ 出口调色：全场过同一道 ═══
  // 水彩化：饱和 ×0.8、明度上浮 7%（模拟颜料兑水）。这是让不同时期画的资产
  // 看起来出自同一支笔的最省力手段。⚠️ 色板表里的值是**过滤前**的原色，
  // 不要手工预先调淡，否则会被调两次。
  const WC_CACHE = Object.create(null);
  function wcTone(hex) {
    if (WC_CACHE[hex]) return WC_CACHE[hex];
    let r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    let h = 0, l = (mx + mn) / 2;
    let s = mx === mn ? 0 : (mx - mn) / (1 - Math.abs(2 * l - 1));
    if (mx !== mn) {
      if (mx === r) h = ((g - b) / (mx - mn)) % 6;
      else if (mx === g) h = (b - r) / (mx - mn) + 2;
      else h = (r - g) / (mx - mn) + 4;
      h = (h * 60 + 360) % 360;
    }
    s *= 0.8; l += (1 - l) * 0.07;
    const K = (1 - Math.abs(2 * l - 1)) * s, X = K * (1 - Math.abs((h / 60) % 2 - 1)), m = l - K / 2;
    const [R, G, B] = h < 60 ? [K, X, 0] : h < 120 ? [X, K, 0] : h < 180 ? [0, K, X]
      : h < 240 ? [0, X, K] : h < 300 ? [X, 0, K] : [K, 0, X];
    const to = v => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return (WC_CACHE[hex] = '#' + to(R) + to(G) + to(B));
  }
  const wcArt = svg => svg.replace(/#[0-9a-fA-F]{6}\b/g, wcTone);

  // ═══ 光向：左上 45°，全场唯一 ═══
  // 受光面与背光面是**盖在固有色上的半透明片**，不是另一个填充色——这样同一段
  // 覆盖代码能贴到任何底色上，不必为每种材质各配一对色。
  const LIT = 'fill="#ffffff" opacity="0.16"';        // 受光面（平板形体）
  const DARK = 'fill="#1e2c18" opacity="0.13"';       // 背光面（平板形体）
  const px = n => +(+n).toFixed(2);

  // 球体：暗底 → 固有 → 受光 → 高光点。偏移量即规范里的 0.26r/0.30r 与 0.34r/0.40r。
  // flat<1 表示压扁（南瓜、石子）。
  function orb(cx, cy, r, base, opts) {
    opts = opts || {};
    const lit = opts.lit || mixc(base, '#ffffff', 0.28);
    const dark = opts.dark || mixc(base, '#2a1c10', 0.22);
    const k = opts.flat || 1;
    return `<ellipse cx="${px(cx)}" cy="${px(cy)}" rx="${px(r)}" ry="${px(r * k)}" fill="${dark}"/>`
      + `<ellipse cx="${px(cx - r * 0.1)}" cy="${px(cy - r * 0.12 * k)}" rx="${px(r * 0.88)}" ry="${px(r * 0.86 * k)}" fill="${base}"/>`
      + `<ellipse cx="${px(cx - r * 0.26)}" cy="${px(cy - r * 0.3 * k)}" rx="${px(r * 0.5)}" ry="${px(r * 0.44 * k)}" fill="${lit}"/>`
      + `<ellipse cx="${px(cx - r * 0.34)}" cy="${px(cy - r * 0.4 * k)}" rx="${px(r * 0.22)}" ry="${px(r * 0.16 * k)}" fill="#ffffff" opacity="0.55"/>`;
  }

  // 哑光球：与 orb 同一光向，但**没有高光点**。麻袋、陶土、石头这类不反光的材质用它——
  // orb 那颗 opacity .55 的白点是给果实/釉面用的，贴到粗麻布上就成了气球。
  function dome(cx, cy, r, base, opts) {
    opts = opts || {};
    const k = opts.flat || 1;
    return `<ellipse cx="${px(cx)}" cy="${px(cy)}" rx="${px(r)}" ry="${px(r * k)}" fill="${base}"/>`
      + `<path d="M${px(cx)},${px(cy - r * k)} A${px(r)},${px(r * k)} 0 0 0 ${px(cx - r * 0.87)},${px(cy + r * k * 0.5)} Z" ${LIT}/>`
      + `<path d="M${px(cx)},${px(cy + r * k)} A${px(r)},${px(r * k)} 0 0 0 ${px(cx + r * 0.87)},${px(cy - r * k * 0.5)} Z" ${DARK}/>`;
  }

  // ═══ 接地阴影 ═══
  // 判据：这个东西在现实里会碰到地面，它就必须有。悬空物（蝶/蜂/灯串/气泡）不画。
  // PITCH 是全场统一的地面椭圆比，等价于相机俯角约 16°——所有画在地面上的椭圆
  // （水塘、土面、光斑、阴影）都用它，不许各画各的。
  const PITCH = 0.28;
  const ground = (cx, cy, rx, op) =>
    `<ellipse cx="${px(cx)}" cy="${px(cy)}" rx="${px(rx)}" ry="${px(rx * PITCH)}" fill="#1e150c" opacity="${op == null ? 0.14 : op}"/>`;

  // ═══ 描边：只描人造物 ═══
  // 自然物（植株、动物、水、云、土）不描边，靠三面法拉开层次。
  // w 应取该画稿 viewBox 短边的 0.7%。
  const stroke = w => `stroke="${C.ink.base}" stroke-opacity="0.4" stroke-width="${px(w)}" stroke-linejoin="round"`;

  return { C, mixc, wcTone, wcArt, orb, dome, ground, stroke, LIT, DARK, PITCH, px };
})();

if (typeof module !== 'undefined') module.exports = ART;
